import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useRoomContext } from "@livekit/components-react";
import { RoomEvent, Track, VideoQuality } from "livekit-client";

const CAMERA_QUALITY_TIERS = [
  { maxCameras: 4, quality: VideoQuality.HIGH },
  { maxCameras: 12, quality: VideoQuality.MEDIUM },
];

const TIER_EVENTS = [
  RoomEvent.ParticipantConnected,
  RoomEvent.ParticipantDisconnected,
  RoomEvent.TrackPublished,
  RoomEvent.TrackUnpublished,
  RoomEvent.TrackSubscribed,
  RoomEvent.TrackMuted,
  RoomEvent.TrackUnmuted,
];

const qualityForCameraCount = (count) =>
  CAMERA_QUALITY_TIERS.find((tier) => count <= tier.maxCameras)?.quality ?? VideoQuality.LOW;

const focusKey = (identity, source) => `${identity}:${source}`;

const VideoQualityFocusContext = createContext(null);

export const VideoQualityTierProvider = ({ children }) => {
  const room = useRoomContext();
  const [focusedTracks, setFocusedTracks] = useState(() => new Map());

  const registerFocus = useCallback((key) => {
    setFocusedTracks((prev) => {
      const next = new Map(prev);
      next.set(key, (next.get(key) || 0) + 1);
      return next;
    });
    return () => {
      setFocusedTracks((prev) => {
        const next = new Map(prev);
        const count = (next.get(key) || 0) - 1;
        if (count > 0) {
          next.set(key, count);
        } else {
          next.delete(key);
        }
        return next;
      });
    };
  }, []);

  useEffect(() => {
    if (!room) return;

    const applyQuality = () => {
      const remoteParticipants = Array.from(room.remoteParticipants.values());
      const activeCameras = remoteParticipants.filter((participant) => {
        const publication = participant.getTrackPublication(Track.Source.Camera);
        return publication && !publication.isMuted;
      }).length;
      const tierQuality = qualityForCameraCount(activeCameras);

      remoteParticipants.forEach((participant) => {
        participant.videoTrackPublications.forEach((publication) => {
          if (!publication.isSubscribed) return;
          const isFocused = focusedTracks.has(focusKey(participant.identity, publication.source));
          publication.setVideoQuality(isFocused ? VideoQuality.HIGH : tierQuality);
        });
      });
    };

    applyQuality();
    TIER_EVENTS.forEach((event) => room.on(event, applyQuality));
    return () => {
      TIER_EVENTS.forEach((event) => room.off(event, applyQuality));
    };
  }, [room, focusedTracks]);

  return (
    <VideoQualityFocusContext.Provider value={registerFocus}>
      {children}
    </VideoQualityFocusContext.Provider>
  );
};

export const useVideoQualityFocus = (participant, sources, enabled) => {
  const registerFocus = useContext(VideoQualityFocusContext);
  const identity = participant && !participant.isLocal ? participant.identity : null;
  const sourcesKey = sources.join(",");

  useEffect(() => {
    if (!registerFocus || !enabled || !identity) return;
    const unregisters = sourcesKey
      .split(",")
      .map((source) => registerFocus(focusKey(identity, source)));
    return () => unregisters.forEach((unregister) => unregister());
  }, [registerFocus, enabled, identity, sourcesKey]);
};
